import mongoose from "mongoose";
import cron from "node-cron";

import { Class } from "../models/class";
import { logger } from "../utils/logger";
import { notificationService } from "./notification-service";

/**
 * Service to send attendance reminders before classes
 */
class AttendanceReminderService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private cronJob: any = null;
  private reminderMinutes = 30; // Send reminder 30 minutes before class

  /**
   * Start the attendance reminder cron job
   * Runs every 5 minutes to check for upcoming classes
   */
  start(): void {
    if (this.cronJob) {
      logger.warn("Attendance reminder service is already running");
      return;
    }

    // Run every 5 minutes
    this.cronJob = cron.schedule("*/5 * * * *", async () => {
      await this.checkUpcomingClasses();
    });

    logger.info("Attendance reminder service started");
  }

  /**
   * Stop the attendance reminder cron job
   */
  stop(): void {
    if (this.cronJob) {
      this.cronJob.stop();
      this.cronJob = null;
      logger.info("Attendance reminder service stopped");
    }
  }

  /**
   * Check for upcoming classes and send reminders
   */
  private async checkUpcomingClasses(): Promise<void> {
    try {
      const now = new Date();
      const reminderTime = new Date(
        now.getTime() + this.reminderMinutes * 60 * 1000,
      );
      const windowEnd = new Date(
        now.getTime() + (this.reminderMinutes + 5) * 60 * 1000,
      );

      // Find classes that start within the reminder window
      const upcomingClasses = await Class.find({
        status: "active",
        // Match classes where schedule has a day matching today and time within window
        $or: [
          {
            "schedule.day": this.getDayOfWeek(reminderTime),
            "schedule.startTime": {
              $gte: this.getTimeString(reminderTime),
              $lt: this.getTimeString(windowEnd),
            },
          },
        ],
      }).populate("schoolId", "schoolName");

      logger.info(
        `Found ${upcomingClasses.length} upcoming classes for reminders`,
      );

      // Send reminders for each class
      for (const classItem of upcomingClasses) {
        try {
          // Check if reminder was already sent (using metadata or cache)
          const classId = classItem._id.toString();
          const reminderKey = `attendance_reminder:${classId}:${this.getDateKey(reminderTime)}`;

          // You can implement Redis caching here to avoid duplicate reminders
          // For now, we'll send the reminder

          const schedule = classItem.schedule;
          if (!schedule || schedule.length === 0) {
            continue;
          }

          const classTime = this.getClassDateTime(
            reminderTime,
            schedule[0].startTime,
          );

          await notificationService.notifyUpcomingAttendance(
            classItem._id,
            classItem.schoolId,
            classItem.className,
            classTime,
            this.reminderMinutes,
          );

          logger.info(
            `Sent attendance reminder for class: ${classItem.className}`,
          );
        } catch (error) {
          logger.error(
            `Error sending reminder for class ${classItem._id.toString()}:`,
            error,
          );
        }
      }
    } catch (error) {
      logger.error("Error checking upcoming classes:", error);
    }
  }

  /**
   * Get day of week (0 = Sunday, 6 = Saturday)
   */
  private getDayOfWeek(date: Date): number {
    return date.getDay();
  }

  /**
   * Get time string in HH:MM format
   */
  private getTimeString(date: Date): string {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  /**
   * Get date key for caching (YYYY-MM-DD-HH)
   */
  private getDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hour = date.getHours().toString().padStart(2, "0");
    return `${year}-${month}-${day}-${hour}`;
  }

  /**
   * Get class date time by combining current date with class time
   */
  private getClassDateTime(date: Date, timeString: string): Date {
    const [hours, minutes] = timeString.split(":").map(Number);
    const classDate = new Date(date);
    classDate.setHours(hours, minutes, 0, 0);
    return classDate;
  }

  /**
   * Manually trigger reminder for a specific class (for testing)
   */
  async sendManualReminder(
    classId: string,
    schoolId: string,
    className: string,
    classTime: Date,
  ): Promise<void> {
    await notificationService.notifyUpcomingAttendance(
      mongoose.Types.ObjectId.createFromHexString(classId),
      mongoose.Types.ObjectId.createFromHexString(schoolId),
      className,
      classTime,
      this.reminderMinutes,
    );
  }
}

export const attendanceReminderService = new AttendanceReminderService();
